import { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  FormControl,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/WarningAmber';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatBytes } from '@/utils/formatBytes';
import { Check } from '@mui/icons-material';

const getInitialValues = () => {
  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const defaultMonth = prevMonthDate.getMonth() + 1;
  const defaultYear = prevMonthDate.getFullYear();
  return { defaultMonth, defaultYear };
};

export default function InvoiceUploadDialog({
  open,
  onClose,
  onUpload,
  config,
  existingFiles,
}) {
  const [rows, setRows] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);
  const { defaultMonth, defaultYear } = useMemo(
    () => getInitialValues(),
    [open],
  );

  const processFiles = (files) => {
    const fileArray = Array.from(files);
    const newRows = fileArray.map((file, index) => ({
      id: `${file.name}-${Date.now()}-${index}`,
      fileObj: file,
      filename: file.name,
      size: file.size,
      uploadDate: new Date(),
      templateId: '',
      month: defaultMonth,
      year: defaultYear,
      isConfig: false,
    }));

    setRows((prev) => [...prev, ...newRows]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading) setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading) setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive && !loading) setIsDragActive(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (loading) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
    e.target.value = null;
  };

  const handleBoxClick = () => {
    if (!loading) {
      fileInputRef.current?.click();
    }
  };

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const deleteRow = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const isSaveDisabled = rows.length === 0 || rows.some((r) => !r.templateId);

  const checkCollision = (row) => {
    if (!row.templateId) return false;
    const template = config.templates.find((t) => t.id === row.templateId);
    if (!template) return false;
    const ext = row.filename.split('.').pop();
    let targetFilename;
    if (template.isConfig) {
      targetFilename = `msp/config/${template.name}.${ext}`;
    } else {
      if (!row.month || !row.year) return false;
      const monthStr = String(row.month).padStart(2, '0');
      targetFilename = `msp/${monthStr}-${row.year}/${template.name}.${ext}`;
    }
    return existingFiles?.some((f) => f.filename === targetFilename) ?? false;
  };

  const columns = [
    { field: 'filename', headerName: 'Dateiname', flex: 1, minWidth: 200 },
    {
      field: 'templateId',
      headerName: 'Vorlage',
      width: 350,
      renderCell: (params) => {
        const fileExt = params.row.filename.split('.').pop().toLowerCase();
        return (
          <FormControl fullWidth size="small" margin="dense">
            <Select
              value={params.value}
              displayEmpty
              disabled={loading}
              onChange={(e) => {
                const tpl = config.templates.find(
                  (t) => t.id === e.target.value,
                );
                updateRow(params.row.id, 'templateId', e.target.value);
                updateRow(params.row.id, 'isConfig', tpl?.isConfig || false);
              }}
            >
              <MenuItem value="" disabled>
                <em>Bitte wählen</em>
              </MenuItem>
              {config?.templates?.length > 0 &&
                config.templates.map((tpl) => {
                  const isFormatValid = tpl.allowedFormats.includes(fileExt);
                  return (
                    <MenuItem
                      key={tpl.id}
                      value={tpl.id}
                      disabled={!isFormatValid}
                    >
                      {tpl.name}{' '}
                      {!isFormatValid &&
                        `(nur ${tpl.allowedFormats.join(', ')})`}
                      {tpl.isConfig
                        ? ' (Konfiguration)'
                        : isFormatValid &&
                          ` (${tpl.period === 'P1M' ? 'Monatlich' : tpl.period === 'P1Y' ? 'Jährlich' : '-'})`}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'isConfig',
      headerName: 'Konfig',
      width: 80,
      renderCell: (params) =>
        params.row.isConfig ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Check />
          </Box>
        ) : (
          ''
        ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: 'month',
      headerName: 'Monat',
      width: 100,
      renderCell: (params) => (
        <Select
          fullWidth
          size="small"
          disabled={loading || params.row.isConfig}
          value={!params.row.isConfig && params.value}
          onChange={(e) => updateRow(params.row.id, 'month', e.target.value)}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'year',
      headerName: 'Jahr',
      width: 120,
      renderCell: (params) => (
        <Select
          fullWidth
          size="small"
          disabled={loading || params.row.isConfig}
          value={!params.row.isConfig && params.value}
          onChange={(e) => updateRow(params.row.id, 'year', e.target.value)}
        >
          {[defaultYear - 1, defaultYear, defaultYear + 1].map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'size',
      headerName: 'Größe',
      width: 120,
      valueGetter: (params) => formatBytes(params || 0),
    },
    {
      field: 'uploadDate',
      headerName: 'Datum',
      width: 120,
      valueFormatter: (params) => new Date(params).toLocaleDateString('de-DE'),
    },
    {
      field: 'status',
      headerName: '',
      width: 50,
      renderCell: (params) => {
        const hasCollision = checkCollision(params.row);
        if (hasCollision) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Tooltip title="Datei existiert bereits für diesen Zeitraum und wird überschrieben.">
                <WarningIcon color="warning" />
              </Tooltip>
            </Box>
          );
        }
        return null;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 65,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={() => deleteRow(params.row.id)}
          disabled={loading}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const handleSave = async () => {
    const preparedData = rows.map((row) => {
      const template = config?.templates?.find((t) => t.id === row.templateId);
      return {
        file: row.fileObj,
        templateName: template ? template.name : 'Unknown',
        month: row.isConfig ? undefined : row.month,
        year: row.isConfig ? undefined : row.year,
        size: row.size,
        isConfig: template?.isConfig || false,
      };
    });

    setLoading(true);
    try {
      await onUpload(preparedData);
      setRows([]);
      onClose();
    } catch (error) {
      console.error('Fehler beim Upload im Dialog:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>Eingangsrechnungen hochladen</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ height: 400, width: '100%' }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            hideFooter
            rowHeight={60}
            sx={{
              '& .MuiDataGrid-cell': {
                overflow: 'visible',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              opacity: loading ? 0.5 : 1,
              pointerEvents: loading ? 'none' : 'auto',
            }}
          />
        </Box>
        <Box
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBoxClick}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: isDragActive ? '#f0f8ff' : '#fafafa',
            borderColor: isDragActive ? '#1976d2' : '#ccc',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            mt: 2,
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
        >
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={loading}
          />
          <CloudUploadIcon
            sx={{
              fontSize: 40,
              color: isDragActive ? 'primary.main' : 'text.secondary',
              mb: 1,
            }}
          />
          <Typography
            variant="h6"
            color={isDragActive ? 'primary.main' : 'text.secondary'}
          >
            {isDragActive
              ? 'Dateien hier loslassen'
              : 'Dateien hierher ziehen oder klicken zum Auswählen'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveDisabled || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {loading ? 'Speichert...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
